import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

const Select = ({ value, onValueChange, children, placeholder, className = '' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const content = React.Children.toArray(children).find(
        (child) => child?.type === SelectContent
    );
    const items = React.Children.toArray(content?.props?.children);
    const selectedChild = items.find(
        (item) => item?.props?.value === value
    );

    return (
        <div className={`relative ${className}`} ref={containerRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="flex h-11 w-full items-center justify-between rounded-lg bg-input-bg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sm-primary"
            >
                <span className={!value ? 'text-text-gray' : 'text-primary-text'}>
                    {selectedChild ? selectedChild.props.children : placeholder}
                </span>
                <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
                    {React.Children.map(children, child =>
                        child.type === SelectContent ? React.cloneElement(child, { onValueChange, setIsOpen }) : child
                    )}
                </div>
            )}
        </div>
    );
};

const SelectContent = ({ children, onValueChange, setIsOpen }) => {
    return (
        <>
            {React.Children.map(children, (child) => {
                if (!React.isValidElement(child)) return null;
                return React.cloneElement(child, {
                    onClick: () => {
                        onValueChange(child.props.value);
                        setIsOpen(false);
                    }
                });
            })}
        </>
    );
};

const SelectItem = ({ children, value, onClick, className = '' }) => {
    return (
        <div
            onClick={onClick}
            className={`relative flex w-full select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-gray-bg focus:bg-gray-bg cursor-pointer ${className}`}
        >
            {children}
        </div>
    );
};

const SelectValue = () => null;
const SelectTrigger = () => null;

export { Select, SelectContent, SelectItem, SelectValue, SelectTrigger };
